import { useState, useRef, Children } from "react";
import type { ReactNode } from "react";
import {
  Box,
  Container,
  Flex,
  FlexProps,
  IconButton,
  StyleProps,
  useColorModeValue,
  useDimensions,
  useDisclosure,
  useMediaQuery,
} from "@chakra-ui/react";

import { MdFilterList, MdMessage } from "react-icons/md";

interface SidebarCollapseProps {
  defaultIsOpen?: boolean;
  topBar?: ReactNode;
  sideBar?: ReactNode;
  mainBar?: ReactNode;
}

export const StickyTopBar = (props: {
  children: ReactNode;
  isFullWidth?: boolean;
  sx?: FlexProps;
}) => {
  const refStickyTopBar = useRef<HTMLDivElement>(null);
  const stickyTopBarDimensions = useDimensions(refStickyTopBar, true);

  const bgGlassNav = useColorModeValue("whiteAlpha.900", "rgba(4, 4, 40, 0.8)");
  const bgNormalNav = useColorModeValue("transparent", "transparent");

  return (
    <Flex
      ref={refStickyTopBar}
      position={{ base: "initial", md: "sticky" }}
      alignItems={"flex-start"}
      py={{ base: 4, md: 4 }}
      top={{ base: "89px", md: "89px" }}
      width={"full"}
      zIndex={
        stickyTopBarDimensions &&
        stickyTopBarDimensions.borderBox.top <= 89 &&
        stickyTopBarDimensions.borderBox.top !== 0
          ? "sticky"
          : "1"
      }
      my={8}
      {...props.sx}
      backgroundColor={
        props.sx?.bgColor && props.sx.bgColor !== "auto"
          ? props.sx.bgColor
          : stickyTopBarDimensions &&
            stickyTopBarDimensions.borderBox.top <= 89 &&
            stickyTopBarDimensions.borderBox.top !== 0
          ? bgGlassNav
          : bgNormalNav
      }
    >
      <Container
        maxW={props.isFullWidth ? "full" : "container.2xl"}
        px={props.isFullWidth ? 0 : 4}
      >
        {props.children}
      </Container>
    </Flex>
  );
};

const SidebarCollapse = (props: SidebarCollapseProps): JSX.Element => {
  const { defaultIsOpen = false, topBar, sideBar, mainBar } = props;

  const { isOpen: filterIsOpen, onToggle: onFilterToggle } = useDisclosure({
    defaultIsOpen: defaultIsOpen,
  });
  const bgColor = useColorModeValue("white", "main.darkblue");

  return (
    <Box>
      <StickyTopBar>
        {/* Trigger Sidebar */}
        {/* <IconButton
          variant={"input"}
          size={"md"}
          aria-label="Show/Hide Panel"
          icon={<MdFilterList />}
          onClick={onFilterToggle}
          mr={{ base: 2, md: 4 }}
        /> */}
        <Box ml={"auto"} flexGrow={1}>
          {topBar}
        </Box>
      </StickyTopBar>

      <Flex
        display={{ base: "block", md: "flex" }}
        direction={"row"}
        overflow={"hidden"}
        position={"relative"}
        gap={4}
      >
        <Box
          height={"full"}
          maxHeight={"full"}
          width={{ base: "100%", md: "320px" }}
          flexBasis={{ base: "100%", md: "320px" }}
          position={{ base: "fixed", lg: "relative" }}
          hidden={!filterIsOpen ? true : false}
          pr={{ base: 0, md: 4 }}
          boxShadow={{ base: "lg", lg: "none" }}
          zIndex={2}
        >
          <Box
            width={"full"}
            height={{ base: "calc(100vh - 120px)", lg: "full" }}
            top={0}
            px={{ base: 2, md: 0 }}
            overflowY={"hidden"}
          >
            {sideBar}
          </Box>
        </Box>

        <Container maxW={"full"} flex={1} position={"relative"} px={0}>
          {mainBar}
        </Container>
      </Flex>
    </Box>
  );
};

export default SidebarCollapse;
